import React, { useCallback, useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

async function parseApiError(res) {
  try {
    const data = await res.json();
    return data?.error || data?.message || `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

function formatRequestedAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function approvalCount(approvals) {
  const approved = (approvals || []).filter((a) => String(a.decision).toLowerCase() === 'approved').length;
  return `${approved}/2`;
}

export default function PhoneChangeApprovalPanel({ authToken, onQueueChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/phone-change/admin-pending`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      setItems([]);
      setError(err.message || 'Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const postDecision = async (requestId, body) => {
    const res = await fetch(`${API_BASE_URL}/api/phone-change/admin-decision/${requestId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await parseApiError(res));
    return res.json();
  };

  const handleApprove = async (requestId) => {
    setBusyId(requestId);
    setError('');
    try {
      await postDecision(requestId, { decision: 'approved' });
      await load();
      if (onQueueChange) onQueueChange();
    } catch (err) {
      setError(err.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (requestId) => {
    const note = window.prompt('Rejection reason:');
    if (note === null) return;
    const trimmed = note.trim();
    if (!trimmed) {
      setError('Rejection reason is required');
      return;
    }
    setBusyId(requestId);
    setError('');
    try {
      await postDecision(requestId, { decision: 'rejected', note: trimmed });
      await load();
      if (onQueueChange) onQueueChange();
    } catch (err) {
      setError(err.message || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading phone change requests…</p>;
  }

  return (
    <div className="space-y-3">
      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No pending phone change requests</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-amber-500/20">
          <table className="w-full min-w-[720px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-amber-500/20 bg-amber-500/5 text-amber-200/80">
                <th className="px-3 py-2 font-semibold">User Name</th>
                <th className="px-3 py-2 font-semibold">Role</th>
                <th className="px-3 py-2 font-semibold">Old Number</th>
                <th className="px-3 py-2 font-semibold">New Number</th>
                <th className="px-3 py-2 font-semibold">Reason</th>
                <th className="px-3 py-2 font-semibold">Requested At</th>
                <th className="px-3 py-2 font-semibold">Approvals</th>
                <th className="px-3 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-white/5 text-muted-foreground">
                  <td className="px-3 py-2 font-medium text-amber-100">{row.user?.name || '—'}</td>
                  <td className="px-3 py-2 capitalize">{row.role || '—'}</td>
                  <td className="px-3 py-2">{row.old_phone || '—'}</td>
                  <td className="px-3 py-2">{row.new_phone || '—'}</td>
                  <td className="px-3 py-2 max-w-[140px] truncate" title={row.reason || ''}>{row.reason || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatRequestedAt(row.requested_at)}</td>
                  <td className="px-3 py-2 font-semibold text-amber-200">{approvalCount(row.approvals)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-bold text-white disabled:opacity-50"
                        disabled={busyId === row.id}
                        onClick={() => handleApprove(row.id)}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="rounded-md bg-red-600/90 px-3 py-1 text-xs font-bold text-white disabled:opacity-50"
                        disabled={busyId === row.id}
                        onClick={() => handleReject(row.id)}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
